import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Filter,
  Gauge,
  Home,
  LogOut,
  Menu,
  RefreshCw,
  Search,
  Settings2,
  Sparkles,
  WalletCards,
  Webhook,
  X,
} from 'lucide-react';
import { adminLogout, api, isAuthenticated } from '../lib/api';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const pct = (value) => `${Number(value || 0).toFixed(1)}%`;
const short = (value, left = 8) => (value ? `${String(value).slice(0, left)}${String(value).length > left ? '...' : ''}` : '-');
const dateTime = (value) => (value ? new Date(value).toLocaleString() : '-');
const secondsLabel = (seconds) => {
  if (seconds == null) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
};

const statusTone = (status) => {
  if (status === 'SUCCESS') return 'bg-emerald-400/15 text-emerald-200 ring-emerald-300/35';
  if (status === 'PENDING') return 'bg-amber-400/15 text-amber-200 ring-amber-300/35';
  if (['FAILED', 'EXPIRED', 'CANCELLED', 'REFUNDED'].includes(status)) return 'bg-rose-400/15 text-rose-200 ring-rose-300/35';
  return 'bg-slate-700/50 text-slate-200 ring-white/10';
};

const callbackTone = (state) => {
  if (state === 'sent') return 'bg-emerald-400/15 text-emerald-200 ring-emerald-300/35';
  if (state === 'retrying') return 'bg-cyan-400/15 text-cyan-200 ring-cyan-300/35';
  if (state === 'failed') return 'bg-rose-400/15 text-rose-200 ring-rose-300/35';
  if (state === 'pending') return 'bg-amber-400/15 text-amber-200 ring-amber-300/35';
  return 'bg-violet-400/15 text-violet-200 ring-violet-300/35';
};

const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, tone: 'emerald' },
  { id: 'transactions', label: 'Transactions', icon: WalletCards, tone: 'cyan' },
  { id: 'methods', label: 'Methods', icon: CreditCard, tone: 'violet' },
  { id: 'clients', label: 'Clients', icon: Building2, tone: 'amber' },
  { id: 'callbacks', label: 'Callbacks', icon: Webhook, tone: 'rose' },
  { id: 'reports', label: 'Reports', icon: BarChart3, tone: 'blue' },
  { id: 'advanced', label: 'Advanced', icon: Settings2, tone: 'slate' },
];

const toneClasses = {
  emerald: {
    soft: 'bg-emerald-400/15 text-emerald-200 ring-emerald-300/30',
    panel: 'from-emerald-400/18 via-emerald-300/8 to-transparent',
    line: 'bg-emerald-300',
    text: 'text-emerald-200',
  },
  cyan: {
    soft: 'bg-cyan-400/15 text-cyan-200 ring-cyan-300/30',
    panel: 'from-cyan-400/18 via-cyan-300/8 to-transparent',
    line: 'bg-cyan-300',
    text: 'text-cyan-200',
  },
  violet: {
    soft: 'bg-violet-400/15 text-violet-200 ring-violet-300/30',
    panel: 'from-violet-400/18 via-violet-300/8 to-transparent',
    line: 'bg-violet-300',
    text: 'text-violet-200',
  },
  amber: {
    soft: 'bg-amber-400/15 text-amber-200 ring-amber-300/30',
    panel: 'from-amber-400/18 via-amber-300/8 to-transparent',
    line: 'bg-amber-300',
    text: 'text-amber-200',
  },
  rose: {
    soft: 'bg-rose-400/15 text-rose-200 ring-rose-300/30',
    panel: 'from-rose-400/18 via-rose-300/8 to-transparent',
    line: 'bg-rose-300',
    text: 'text-rose-200',
  },
  blue: {
    soft: 'bg-blue-400/15 text-blue-200 ring-blue-300/30',
    panel: 'from-blue-400/18 via-blue-300/8 to-transparent',
    line: 'bg-blue-300',
    text: 'text-blue-200',
  },
  slate: {
    soft: 'bg-slate-400/10 text-slate-200 ring-white/10',
    panel: 'from-slate-400/12 via-white/5 to-transparent',
    line: 'bg-slate-300',
    text: 'text-slate-200',
  },
};

const defaultFilters = {
  days: '30',
  status: '',
  method: '',
  provider: '',
  clientId: '',
  callback: '',
  q: '',
  amountMin: '',
  amountMax: '',
};

export default function GatewayAdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filters, setFilters] = useState(defaultFilters);
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [methods, setMethods] = useState([]);
  const [methodSummary, setMethodSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clientModal, setClientModal] = useState(null);
  const [snippetClient, setSnippetClient] = useState(null);
  const [legacy, setLegacy] = useState({ loaded: false, tab: 'checkouts', checkouts: [], mailboxes: [], events: [], claims: [] });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4500);
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value || '');
      showNotification('success', 'Copied');
    } catch {
      showNotification('error', 'Copy failed');
    }
  };

  const loadGateway = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(nextFilters).filter(([, v]) => v !== ''));
      const [overviewRes, txRes, methodsRes, clientsRes] = await Promise.all([
        api.gatewayOverview(params),
        api.gatewayTransactions({ ...params, limit: 250 }),
        api.gatewayMethods(),
        api.listClients().catch(() => []),
      ]);
      setOverview(overviewRes);
      setTransactions(txRes.items || []);
      setMethods(methodsRes.methods || []);
      setMethodSummary(methodsRes.summary || null);
      setClients(Array.isArray(clientsRes) ? clientsRes : []);
    } catch (err) {
      showNotification('error', err.message || 'Failed to load gateway data');
    } finally {
      setLoading(false);
    }
  };

  const loadLegacy = async () => {
    try {
      const [checkoutsRes, mailboxesRes, eventsRes, claimsRes] = await Promise.all([
        api.listCheckouts(),
        api.listMailboxes(),
        api.listMailEvents({ limit: 50 }),
        api.listClaims({ pending_only: true }),
      ]);
      setLegacy({
        loaded: true,
        tab: legacy.tab,
        checkouts: checkoutsRes.checkouts || [],
        mailboxes: mailboxesRes.mailboxes || [],
        events: eventsRes.events || [],
        claims: claimsRes.claims || [],
      });
    } catch (err) {
      showNotification('error', err.message || 'Failed to load advanced tools');
    }
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }

    if (searchParams.get('success') || searchParams.get('error')) {
      setActiveTab('advanced');
      setLegacy((prev) => ({ ...prev, tab: 'mailboxes' }));
    }

    loadGateway();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  useEffect(() => {
    if (activeTab === 'advanced' && !legacy.loaded) {
      loadLegacy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const applyFilters = () => loadGateway(filters);
  const clearFilters = () => {
    setFilters(defaultFilters);
    loadGateway(defaultFilters);
  };

  const openDetail = async (txId) => {
    setDetailLoading(true);
    try {
      const res = await api.gatewayTransaction(txId);
      setDetail(res);
    } catch (err) {
      showNotification('error', err.message || 'Failed to load transaction');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSignOut = () => {
    adminLogout();
    navigate('/admin/login');
  };

  const saveClient = async (payload, editing) => {
    if (editing) {
      await api.updateClient(editing.id, payload);
      showNotification('success', 'Client updated');
    } else {
      const created = await api.createClient(payload);
      const [keyRes, secretRes] = await Promise.all([
        api.revealClientSecret(created.id, 'apiKey'),
        api.revealClientSecret(created.id, 'webhookSecret'),
      ]);
      await copy(`API Key: ${keyRes.value}\nWebhook Secret: ${secretRes.value}`);
      showNotification('success', 'Client created');
    }
    setClientModal(null);
    loadGateway();
  };

  const revealClientField = async (client, field) => {
    try {
      const res = await api.revealClientSecret(client.id, field);
      await copy(res.value);
    } catch (err) {
      showNotification('error', err.message);
    }
  };

  const testWebhook = async (client) => {
    try {
      const res = await api.testClientWebhook(client.id);
      showNotification(res.ok ? 'success' : 'error', res.ok ? `Webhook delivered (${res.result?.status || 'OK'})` : 'Webhook failed');
    } catch (err) {
      showNotification('error', err.message);
    }
  };

  const openSnippet = async (client) => {
    try {
      const res = await api.revealClientSecret(client.id, 'apiKey');
      setSnippetClient({ ...client, apiKey: res.value });
    } catch (err) {
      showNotification('error', err.message);
    }
  };

  const toggleClient = async (client) => {
    await api.updateClient(client.id, { isActive: !client.isActive });
    showNotification('success', client.isActive ? 'Client disabled' : 'Client enabled');
    loadGateway();
  };

  const deleteClient = async (client) => {
    if (!window.confirm(`Delete client "${client.name}"?`)) return;
    await api.deleteClient(client.id);
    showNotification('success', 'Client deleted');
    loadGateway();
  };

  const legacyActions = {
    refresh: loadLegacy,
    copy,
    toggleCheckout: async (checkout) => {
      await api.updateCheckout(checkout.id, { is_active: !checkout.is_active });
      showNotification('success', checkout.is_active ? 'Checkout disabled' : 'Checkout enabled');
      loadLegacy();
    },
    deleteCheckout: async (checkout) => {
      if (!window.confirm('Delete this hosted checkout?')) return;
      await api.deleteCheckout(checkout.id);
      showNotification('success', 'Checkout deleted');
      loadLegacy();
    },
    connectMailbox: async (parserType) => {
      const res = await api.startOAuth(parserType);
      if (res.auth_url) window.location.href = res.auth_url;
    },
    syncMailbox: async (mailbox) => {
      const res = await api.syncMailbox(mailbox.id);
      showNotification('success', `Synced ${res.messages_processed || 0} messages`);
      loadLegacy();
    },
    approveClaim: async (claim) => {
      await api.approveClaim(claim.id);
      showNotification('success', 'Claim approved');
      loadLegacy();
    },
    rejectClaim: async (claim) => {
      await api.rejectClaim(claim.id);
      showNotification('success', 'Claim rejected');
      loadLegacy();
    },
  };

  const methodOptions = useMemo(() => [...new Set(methods.map((m) => m.code).filter(Boolean))], [methods]);
  const providerOptions = useMemo(() => [...new Set(methods.map((m) => m.provider).filter(Boolean))], [methods]);

  const activeMeta = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  return (
    <div className="min-h-screen bg-[linear-gradient(135deg,#07070c_0%,#0c1020_48%,#09070f_100%)] text-slate-100">
      <NavDrawer
        open={isNavOpen}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onClose={() => setIsNavOpen(false)}
        accountOpen={accountOpen}
        setAccountOpen={setAccountOpen}
        onSignOut={handleSignOut}
      />

      <div className="relative min-h-screen lg:pl-72">
        <MobileHeader
          title={activeMeta.label}
          onOpenNav={() => setIsNavOpen(true)}
          onRefresh={() => loadGateway()}
          onOpenFilters={() => setIsFilterOpen(true)}
        />

      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded border shadow-xl text-sm ${
          notification.type === 'success' ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200' :
          notification.type === 'error' ? 'bg-rose-500/15 border-rose-500/30 text-rose-200' :
          'bg-cyan-500/15 border-cyan-500/30 text-cyan-200'
        }`}>
          {notification.message}
        </div>
      )}

        <main className="mx-auto max-w-[1500px] px-3 pb-8 pt-4 sm:px-5 lg:px-7 lg:py-7 space-y-5">
          <DesktopPageHeader
            title={activeMeta.label}
            subtitle={`${transactions.length} visible transactions · ${methods.length} methods`}
            tone={activeMeta.tone}
            onRefresh={() => loadGateway()}
          />

          <div className="hidden lg:block">
            <GatewayFilters
              filters={filters}
              setFilters={setFilters}
              clients={clients}
              methods={methodOptions}
              providers={providerOptions}
              onApply={applyFilters}
              onClear={clearFilters}
              loading={loading}
            />
          </div>

        {activeTab === 'dashboard' && <DashboardView overview={overview} loading={loading} onOpenTx={openDetail} />}
        {activeTab === 'transactions' && <TransactionsView rows={transactions} loading={loading} onOpen={openDetail} onCopy={copy} />}
        {activeTab === 'methods' && <MethodsView methods={methods} summary={methodSummary} />}
        {activeTab === 'clients' && (
          <ClientsView
            clients={clients}
            onCreate={() => setClientModal({ editing: null })}
            onEdit={(client) => setClientModal({ editing: client })}
            onReveal={revealClientField}
            onTest={testWebhook}
            onToggle={toggleClient}
            onDelete={deleteClient}
            onSnippet={openSnippet}
          />
        )}
        {activeTab === 'callbacks' && <CallbacksView rows={transactions} onOpen={openDetail} />}
        {activeTab === 'reports' && <ReportsView overview={overview} methods={methods} rows={transactions} onOpen={openDetail} onCopy={copy} />}
        {activeTab === 'advanced' && (
          <AdvancedView
            legacy={legacy}
            setLegacy={setLegacy}
            actions={legacyActions}
          />
        )}
        </main>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          setFilters={setFilters}
          clients={clients}
          methods={methodOptions}
          providers={providerOptions}
          onApply={() => {
            setIsFilterOpen(false);
            applyFilters();
          }}
          onClear={() => {
            setIsFilterOpen(false);
            clearFilters();
          }}
          loading={loading}
        />
      </div>

      {(detail || detailLoading) && (
        <TransactionDrawer
          state={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
          onCopy={copy}
        />
      )}

      {clientModal && (
        <ClientEditor
          editing={clientModal.editing}
          onClose={() => setClientModal(null)}
          onSave={saveClient}
        />
      )}

      {snippetClient && (
        <SnippetModal
          client={snippetClient}
          onClose={() => setSnippetClient(null)}
          onCopy={copy}
        />
      )}
    </div>
  );
}

function NavDrawer({ open, activeTab, setActiveTab, onClose, accountOpen, setAccountOpen, onSignOut }) {
  const drawer = (
    <div className="flex h-full flex-col border-r border-white/10 bg-slate-950/95 px-3 py-4 shadow-2xl backdrop-blur">
      <div className="flex items-center gap-3 px-2">
        <div className="flex h-11 w-11 items-center justify-center rounded border border-emerald-300/30 bg-emerald-400/15 text-emerald-200">
          <Sparkles size={20} />
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold">Gateway Console</h1>
          <p className="truncate text-xs text-slate-500">Production payments</p>
        </div>
        <button onClick={onClose} className="ml-auto rounded p-2 text-slate-400 hover:bg-white/10 hover:text-white lg:hidden" aria-label="Close navigation">
          <X size={18} />
        </button>
      </div>

      <nav className="mt-6 flex-1 space-y-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          const tone = toneClasses[tab.tone] || toneClasses.slate;
          const separated = tab.id === 'advanced';
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                onClose();
              }}
              className={`group relative flex w-full items-center gap-3 rounded px-3 py-3 text-sm transition ${separated ? 'mt-5' : ''} ${
                active ? `bg-white/10 ${tone.text}` : 'text-slate-400 hover:bg-white/5 hover:text-slate-100'
              }`}
            >
              <span className={`absolute left-0 h-6 w-1 rounded-r ${active ? tone.line : 'bg-transparent'}`} />
              <span className={`flex h-8 w-8 items-center justify-center rounded ring-1 ${active ? tone.soft : 'bg-slate-900 text-slate-500 ring-white/10 group-hover:text-slate-200'}`}>
                <Icon size={17} />
              </span>
              <span className="flex-1 text-left">{tab.label}</span>
              {active && <ChevronRight size={16} />}
            </button>
          );
        })}
      </nav>

      <div className="border-t border-white/10 pt-3">
        <button
          onClick={() => setAccountOpen(!accountOpen)}
          className="flex w-full items-center justify-between rounded border border-white/10 bg-white/5 px-3 py-3 text-sm hover:bg-white/10"
        >
          <span>
            <span className="block font-medium text-slate-100">Admin</span>
            <span className="block text-xs text-slate-500">Signed in</span>
          </span>
          <ChevronRight size={16} className={`transition ${accountOpen ? 'rotate-90' : ''}`} />
        </button>
        {accountOpen && (
          <button onClick={onSignOut} className="mt-2 flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-rose-200 hover:bg-rose-400/10">
            <LogOut size={16} />
            Sign out
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 lg:block">{drawer}</aside>
      <div className={`fixed inset-0 z-50 lg:hidden ${open ? '' : 'pointer-events-none'}`}>
        <div className={`absolute inset-0 bg-black/65 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
        <aside className={`absolute inset-y-0 left-0 w-[84vw] max-w-80 transition-transform duration-200 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
          {drawer}
        </aside>
      </div>
    </>
  );
}

function MobileHeader({ title, onOpenNav, onRefresh, onOpenFilters }) {
  return (
    <header className="sticky top-0 z-30 border-b border-white/10 bg-slate-950/90 px-3 py-3 backdrop-blur lg:hidden">
      <div className="flex items-center gap-2">
        <button onClick={onOpenNav} className="rounded border border-white/10 bg-white/5 p-2 text-slate-200" aria-label="Open navigation">
          <Menu size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-emerald-200">Gateway Console</p>
          <h1 className="truncate text-lg font-semibold">{title}</h1>
        </div>
        <button onClick={onOpenFilters} className="rounded border border-cyan-300/20 bg-cyan-400/10 p-2 text-cyan-100" aria-label="Open filters">
          <Filter size={18} />
        </button>
        <button onClick={onRefresh} className="rounded border border-white/10 bg-white/5 p-2 text-slate-200" aria-label="Refresh">
          <RefreshCw size={18} />
        </button>
      </div>
    </header>
  );
}

function DesktopPageHeader({ title, subtitle, tone, onRefresh }) {
  const styles = toneClasses[tone] || toneClasses.emerald;
  return (
    <header className="hidden items-center justify-between gap-4 lg:flex">
      <div>
        <div className={`mb-3 inline-flex items-center gap-2 rounded px-3 py-1 text-xs ring-1 ${styles.soft}`}>
          <Gauge size={14} />
          Gateway health
        </div>
        <h1 className="text-3xl font-semibold tracking-normal">{title}</h1>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      </div>
      <button onClick={onRefresh} className="inline-flex items-center gap-2 rounded border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200 hover:bg-white/10">
        <RefreshCw size={16} />
        Refresh
      </button>
    </header>
  );
}

function FilterDrawer({ open, onClose, filters, setFilters, clients, methods, providers, onApply, onClear, loading }) {
  return (
    <div className={`fixed inset-0 z-50 lg:hidden ${open ? '' : 'pointer-events-none'}`}>
      <div className={`absolute inset-0 bg-black/65 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <aside className={`absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-lg border border-white/10 bg-slate-950 p-3 shadow-2xl transition-transform duration-200 ${open ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-cyan-200">Gateway</p>
            <h2 className="text-lg font-semibold">Filters</h2>
          </div>
          <button onClick={onClose} className="rounded bg-white/10 p-2 text-slate-200" aria-label="Close filters">
            <X size={18} />
          </button>
        </div>
        <GatewayFilters
          filters={filters}
          setFilters={setFilters}
          clients={clients}
          methods={methods}
          providers={providers}
          onApply={onApply}
          onClear={onClear}
          loading={loading}
        />
      </aside>
    </div>
  );
}

function GatewayFilters({ filters, setFilters, clients, methods, providers, onApply, onClear, loading }) {
  const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  return (
    <section className="rounded border border-white/10 bg-slate-950/70 p-3 shadow-xl shadow-black/20">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-9">
        <Select value={filters.days} onChange={(v) => update('days', v)} options={[['7', '7d'], ['30', '30d'], ['90', '90d'], ['365', '1y']]} />
        <Select value={filters.status} onChange={(v) => update('status', v)} options={[['', 'All status'], ['PENDING', 'Pending'], ['SUCCESS', 'Success'], ['FAILED', 'Failed'], ['EXPIRED', 'Expired']]} />
        <Select value={filters.method} onChange={(v) => update('method', v)} options={[['', 'All methods'], ...methods.map((m) => [m, m])]} />
        <Select value={filters.provider} onChange={(v) => update('provider', v)} options={[['', 'All providers'], ...providers.map((p) => [p, p])]} />
        <Select value={filters.clientId} onChange={(v) => update('clientId', v)} options={[['', 'All clients'], ...clients.map((c) => [c.id, c.name])]} />
        <Select value={filters.callback} onChange={(v) => update('callback', v)} options={[['', 'All callbacks'], ['sent', 'Sent'], ['retrying', 'Retrying'], ['failed', 'Failed'], ['pending', 'Pending'], ['no_url', 'No URL']]} />
        <Input value={filters.amountMin} onChange={(v) => update('amountMin', v)} placeholder="Min $" />
        <Input value={filters.amountMax} onChange={(v) => update('amountMax', v)} placeholder="Max $" />
        <div className="flex gap-2">
          <button onClick={onApply} disabled={loading} className="flex-1 rounded bg-emerald-500 px-3 py-2 text-sm font-medium text-slate-950 hover:bg-emerald-300 disabled:opacity-50">
            Apply
          </button>
          <button onClick={onClear} className="rounded bg-white/10 px-3 py-2 text-sm text-slate-200 hover:bg-white/15">
            Clear
          </button>
        </div>
      </div>
      <div className="mt-2">
        <Input value={filters.q} onChange={(v) => update('q', v)} placeholder="Search txId, depositId, order id, client, user, provider status" />
      </div>
    </section>
  );
}

function DashboardView({ overview, loading, onOpenTx }) {
  const summary = overview?.summary || {};
  const byDay = overview?.trends?.byDay || [];
  const byMethod = overview?.trends?.byMethod || [];
  const issues = overview?.issues || [];
  if (loading && !overview) return <LoadingPanel />;
  return (
    <div className="space-y-5">
      <section className="flex gap-3 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible lg:grid-cols-4 xl:grid-cols-8">
        <Metric label="Volume" value={money(summary.totalVolume)} tone="cyan" icon={WalletCards} />
        <Metric label="Paid" value={summary.paidCount || 0} tone="emerald" icon={CheckCircle2} />
        <Metric label="Pending" value={summary.pendingCount || 0} tone="amber" icon={Activity} />
        <Metric label="Failed" value={summary.failedCount || 0} tone="rose" icon={AlertTriangle} />
        <Metric label="Success" value={pct(summary.successRate)} tone="emerald" icon={Gauge} />
        <Metric label="Callbacks" value={pct(summary.callbackSuccessRate)} tone="violet" icon={Webhook} />
        <Metric label="Avg paid" value={secondsLabel(summary.averageTimeToPaidSeconds)} tone="blue" icon={Activity} />
        <Metric label="Fees owed" value={money(summary.feesOwed)} tone="amber" icon={CreditCard} />
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <Panel title="Volume Trend" className="min-w-0 lg:col-span-2">
          <div className="h-72 min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={byDay}>
                <CartesianGrid stroke="#1f2937" vertical={false} />
                <XAxis dataKey="key" stroke="#94a3b8" fontSize={11} tickMargin={8} />
                <YAxis stroke="#94a3b8" fontSize={11} width={42} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,.12)', color: '#fff', borderRadius: 8 }} />
                <Line type="monotone" dataKey="volume" stroke="#22d3ee" strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="count" stroke="#a78bfa" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Method Mix">
          <MiniBars rows={byMethod.slice(0, 8)} valueKey="volume" />
        </Panel>
      </section>

      <Panel title="Flow Issues">
        {issues.length === 0 ? (
          <Empty text="No active flow issues in this window." />
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500">
                  <tr>
                    <th className="text-left py-2">Created</th>
                    <th className="text-left py-2">Client</th>
                    <th className="text-left py-2">Method</th>
                    <th className="text-left py-2">Amount</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Issue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {issues.map((tx) => (
                    <tr key={tx.txId} className="hover:bg-zinc-900 cursor-pointer" onClick={() => onOpenTx(tx.txId)}>
                      <td className="py-2 text-zinc-400">{dateTime(tx.createdAt)}</td>
                      <td className="py-2">{tx.clientName || short(tx.clientId)}</td>
                      <td className="py-2 font-mono text-xs">{tx.method}</td>
                      <td className="py-2">{money(tx.amount)}</td>
                      <td className="py-2"><Badge className={statusTone(tx.status)}>{tx.status}</Badge></td>
                      <td className="py-2 text-amber-300">{tx.issues?.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid gap-3 md:hidden">
              {issues.map((tx) => (
                <button key={tx.txId} onClick={() => onOpenTx(tx.txId)} className="rounded border border-amber-300/20 bg-amber-400/10 p-3 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <Badge className={statusTone(tx.status)}>{tx.status}</Badge>
                    <span className="text-sm font-semibold">{money(tx.amount)}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-200">{tx.clientName || short(tx.clientId)}</p>
                  <p className="mt-1 font-mono text-xs text-slate-400">{tx.method}</p>
                  <p className="mt-2 text-xs text-amber-200">{tx.issues?.join(', ')}</p>
                </button>
              ))}
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

function TransactionsView({ rows, loading, onOpen, onCopy }) {
  if (loading && rows.length === 0) return <LoadingPanel />;
  return (
    <Panel title={`Transactions (${rows.length})`}>
      <TransactionTable rows={rows} onOpen={onOpen} onCopy={onCopy} />
    </Panel>
  );
}

function TransactionTable({ rows, onOpen, onCopy }) {
  if (!rows.length) return <Empty text="No transactions match the current filters." />;
  return (
    <div>
      <div className="grid gap-3 md:hidden">
        {rows.map((tx) => (
          <article key={tx.txId} className="rounded border border-white/10 bg-slate-950/80 p-3 shadow-lg shadow-black/20">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-slate-500">{dateTime(tx.createdAt)}</p>
                <h3 className="mt-1 truncate font-semibold">{tx.clientName || short(tx.clientId)}</h3>
              </div>
              <span className="text-base font-semibold text-cyan-100">{money(tx.amount)}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge className={statusTone(tx.status)}>{tx.status}</Badge>
              <Badge className={callbackTone(tx.callbackState)}>{tx.callbackState}</Badge>
              <Badge className="bg-violet-400/15 text-violet-200 ring-violet-300/30">{tx.providerCode || tx.method}</Badge>
            </div>
            <button onClick={() => onCopy(tx.orderNo || tx.depositId)} className="mt-3 block w-full truncate rounded bg-white/5 px-3 py-2 text-left font-mono text-xs text-slate-300">
              {tx.orderNo || tx.depositId || tx.txId}
            </button>
            <button onClick={() => onOpen(tx.txId)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded bg-cyan-400 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-300">
              Inspect
              <ChevronRight size={16} />
            </button>
          </article>
        ))}
      </div>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-xs text-slate-500">
            <tr>
              <th className="text-left py-2 pr-4">Created</th>
              <th className="text-left py-2 pr-4">Client</th>
              <th className="text-left py-2 pr-4">Method</th>
              <th className="text-left py-2 pr-4">Provider</th>
              <th className="text-left py-2 pr-4">Amount</th>
              <th className="text-left py-2 pr-4">Order</th>
              <th className="text-left py-2 pr-4">Status</th>
              <th className="text-left py-2 pr-4">Callback</th>
              <th className="text-left py-2">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {rows.map((tx) => (
              <tr key={tx.txId} className="hover:bg-white/5">
                <td className="py-3 pr-4 text-slate-400 whitespace-nowrap">{dateTime(tx.createdAt)}</td>
                <td className="py-3 pr-4">{tx.clientName || short(tx.clientId)}</td>
                <td className="py-3 pr-4 font-mono text-xs">{tx.method}</td>
                <td className="py-3 pr-4 font-mono text-xs text-cyan-200">{tx.providerCode}</td>
                <td className="py-3 pr-4">{money(tx.amount)}</td>
                <td className="py-3 pr-4">
                  <button onClick={() => onCopy(tx.orderNo || tx.depositId)} className="font-mono text-xs text-slate-300 hover:text-white">
                    {short(tx.orderNo || tx.depositId, 12)}
                  </button>
                </td>
                <td className="py-3 pr-4"><Badge className={statusTone(tx.status)}>{tx.status}</Badge></td>
                <td className="py-3 pr-4"><Badge className={callbackTone(tx.callbackState)}>{tx.callbackState}</Badge></td>
                <td className="py-3">
                  <button onClick={() => onOpen(tx.txId)} className="rounded bg-white/10 px-3 py-1.5 text-xs hover:bg-white/15">
                    Inspect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MethodsView({ methods, summary }) {
  const visible = methods.filter((m) => !m.legacy);
  const legacy = methods.filter((m) => m.legacy);
  return (
    <div className="space-y-5">
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="Total" value={summary?.total || methods.length} />
        <Metric label="Available" value={summary?.available || 0} tone="emerald" />
        <Metric label="Unavailable" value={summary?.unavailable || 0} tone="red" />
        <Metric label="Disabled" value={summary?.disabled || 0} />
      </section>
      <MethodGrid title="Gateway Methods" methods={visible} />
      <MethodGrid title="Legacy Manual Methods" methods={legacy} />
    </div>
  );
}

function MethodGrid({ title, methods }) {
  return (
    <Panel title={title}>
      {methods.length === 0 ? <Empty text="No methods in this group." /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {methods.map((m) => (
            <div key={m.code} className="border border-zinc-800 rounded bg-zinc-950 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{m.displayName}</h3>
                  <p className="font-mono text-xs text-zinc-500 mt-1">{m.code} · {m.provider}</p>
                </div>
                <Badge className={m.status === 'available' ? statusTone('SUCCESS') : m.status === 'disabled' ? statusTone('PENDING') : statusTone('FAILED')}>
                  {m.status}
                </Badge>
              </div>
              <p className="mt-3 text-sm text-zinc-400">{m.availability}</p>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <Stat label="Volume" value={money(m.metrics?.volume)} />
                <Stat label="Paid" value={m.metrics?.paid || 0} />
                <Stat label="Rate" value={pct(m.metrics?.successRate)} />
              </div>
              <div className="mt-3 text-xs text-zinc-500">
                <p>Last success: <span className="text-zinc-300">{dateTime(m.metrics?.lastSuccessAt)}</span></p>
                <p>Last failure: <span className="text-zinc-300">{dateTime(m.metrics?.lastFailureAt)}</span></p>
              </div>
              {(m.metrics?.activeIssues || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {m.metrics.activeIssues.map((issue) => <Badge key={issue} className="bg-amber-500/15 text-amber-300 ring-amber-500/30">{issue}</Badge>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function ClientsView({ clients, onCreate, onEdit, onReveal, onTest, onToggle, onDelete, onSnippet }) {
  return (
    <Panel
      title={`Clients (${clients.length})`}
      action={<button onClick={onCreate} className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-sm font-medium">Add Client</button>}
    >
      {clients.length === 0 ? <Empty text="No clients configured." /> : (
        <div className="grid lg:grid-cols-2 gap-3">
          {clients.map((client) => (
            <div key={client.id} className="border border-zinc-800 rounded bg-zinc-950 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{client.name}</h3>
                  <p className="font-mono text-xs text-zinc-500 mt-1">{client.id}</p>
                </div>
                <Badge className={client.isActive ? statusTone('SUCCESS') : 'bg-zinc-800 text-zinc-400 ring-zinc-700'}>
                  {client.isActive ? 'ACTIVE' : 'DISABLED'}
                </Badge>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
                <Stat label="Total" value={client.stats?.total || 0} />
                <Stat label="Success" value={client.stats?.success || 0} />
                <Stat label="Pending" value={client.stats?.pending || 0} />
                <Stat label="Failed" value={client.stats?.failed || 0} />
                <Stat label="Last" value={client.stats?.lastActivityAt ? new Date(client.stats.lastActivityAt).toLocaleDateString() : '-'} />
              </div>
              <p className="mt-3 text-xs text-zinc-500 break-all">Webhook: <span className="text-zinc-300">{client.webhookUrl || 'Not configured'}</span></p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => onReveal(client, 'apiKey')} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">Copy API Key</button>
                <button onClick={() => onReveal(client, 'webhookSecret')} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">Copy Secret</button>
                <button onClick={() => onTest(client)} disabled={!client.webhookUrl} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs disabled:opacity-40">Test</button>
                <button onClick={() => onSnippet(client)} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">Snippet</button>
                <button onClick={() => onEdit(client)} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">Edit</button>
                <button onClick={() => onToggle(client)} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">{client.isActive ? 'Disable' : 'Enable'}</button>
                <button onClick={() => onDelete(client)} className="px-3 py-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function CallbacksView({ rows, onOpen }) {
  const callbackRows = rows.filter((tx) => tx.callbackState !== 'waiting');
  const groups = ['sent', 'retrying', 'failed', 'pending', 'no_url'].map((state) => ({
    state,
    count: callbackRows.filter((tx) => tx.callbackState === state).length,
  }));
  return (
    <div className="space-y-5">
      <section className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {groups.map((g) => <Metric key={g.state} label={g.state} value={g.count} tone={g.state === 'failed' ? 'red' : g.state === 'sent' ? 'emerald' : undefined} />)}
      </section>
      <Panel title="Callback Health">
        {callbackRows.length === 0 ? <Empty text="No terminal callback traffic in the current filters." /> : (
          <>
          <div className="grid gap-3 md:hidden">
            {callbackRows.map((tx) => (
              <button key={tx.txId} onClick={() => onOpen(tx.txId)} className="rounded border border-white/10 bg-slate-950/80 p-3 text-left">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-slate-500">{short(tx.txId, 14)}</p>
                    <h3 className="mt-1 truncate font-semibold">{tx.clientName || short(tx.clientId)}</h3>
                  </div>
                  <Badge className={callbackTone(tx.callbackState)}>{tx.callbackState}</Badge>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Stat label="Status" value={tx.status} />
                  <Stat label="Attempts" value={tx.webhookAttempts || 0} />
                  <Stat label="Next retry" value={dateTime(tx.webhookNextRetryAt)} />
                  <Stat label="Error" value={tx.webhookLastError || '-'} />
                </div>
              </button>
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left py-2">Transaction</th>
                  <th className="text-left py-2">Client</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2">Callback</th>
                  <th className="text-left py-2">Attempts</th>
                  <th className="text-left py-2">Next Retry</th>
                  <th className="text-left py-2">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {callbackRows.map((tx) => (
                  <tr key={tx.txId} className="hover:bg-zinc-900/70 cursor-pointer" onClick={() => onOpen(tx.txId)}>
                    <td className="py-3 font-mono text-xs">{short(tx.txId, 14)}</td>
                    <td className="py-3">{tx.clientName || short(tx.clientId)}</td>
                    <td className="py-3"><Badge className={statusTone(tx.status)}>{tx.status}</Badge></td>
                    <td className="py-3"><Badge className={callbackTone(tx.callbackState)}>{tx.callbackState}</Badge></td>
                    <td className="py-3">{tx.webhookAttempts || 0}</td>
                    <td className="py-3 text-zinc-400">{dateTime(tx.webhookNextRetryAt)}</td>
                    <td className="py-3 text-red-300 max-w-xs truncate">{tx.webhookLastError || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Panel>
    </div>
  );
}

function ReportsView({ overview, methods, rows, onOpen, onCopy }) {
  const byProvider = overview?.trends?.byProvider || [];
  const byStatus = overview?.trends?.byStatus || [];
  const topMethods = methods.filter((m) => !m.legacy).slice(0, 10);
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Panel title="Provider Volume">
        <div className="h-72 min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byProvider}>
              <CartesianGrid stroke="#27272a" vertical={false} />
              <XAxis dataKey="key" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', color: '#fff' }} />
              <Bar dataKey="volume" fill="#34d399" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title="Status Mix">
        <MiniBars rows={byStatus} valueKey="count" />
      </Panel>
      <Panel title="Top Methods">
        <MiniBars rows={topMethods.map((m) => ({ key: m.code, volume: m.metrics?.volume || 0, count: m.metrics?.count || 0 }))} valueKey="volume" />
      </Panel>
      <Panel title="Recent Card Deposits">
        <TransactionTable rows={rows.filter((tx) => tx.providerCode === 'card' || tx.method === 'card').slice(0, 8)} onOpen={onOpen} onCopy={onCopy} />
      </Panel>
    </div>
  );
}

function AdvancedView({ legacy, setLegacy, actions }) {
  const setTab = (tab) => setLegacy((prev) => ({ ...prev, tab }));
  const subtabs = [
    ['checkouts', `Hosted Checkouts ${legacy.checkouts.length || ''}`],
    ['mailboxes', `Mailboxes ${legacy.mailboxes.length || ''}`],
    ['events', `Mail Events ${legacy.events.length || ''}`],
    ['claims', `Pending Claims ${legacy.claims.length || ''}`],
  ];
  return (
    <Panel title="Advanced">
      <div className="flex flex-wrap gap-1 border-b border-zinc-800 mb-4">
        {subtabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={`px-3 py-2 text-sm border-b-2 ${legacy.tab === id ? 'text-emerald-300 border-emerald-400' : 'text-zinc-400 border-transparent'}`}>
            {label}
          </button>
        ))}
        <button onClick={actions.refresh} className="ml-auto px-3 py-2 text-sm text-zinc-300 hover:text-white">Refresh</button>
      </div>
      {!legacy.loaded ? <LoadingPanel /> : (
        <>
          {legacy.tab === 'checkouts' && <LegacyCheckouts items={legacy.checkouts} actions={actions} />}
          {legacy.tab === 'mailboxes' && <LegacyMailboxes items={legacy.mailboxes} actions={actions} />}
          {legacy.tab === 'events' && <LegacyEvents items={legacy.events} />}
          {legacy.tab === 'claims' && <LegacyClaims items={legacy.claims} actions={actions} />}
        </>
      )}
    </Panel>
  );
}

function LegacyCheckouts({ items, actions }) {
  if (!items.length) return <Empty text="No hosted checkouts." />;
  return (
    <div className="grid gap-3">
      {items.map((item) => (
        <div key={item.id} className="border border-zinc-800 rounded bg-zinc-950 p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex gap-2 items-center">
              <Badge className={item.is_active ? statusTone('SUCCESS') : 'bg-zinc-800 text-zinc-400 ring-zinc-700'}>{item.is_active ? 'ACTIVE' : 'INACTIVE'}</Badge>
              <span className="font-mono text-xs text-zinc-500">{item.method_code || item.method_type}</span>
            </div>
            <h3 className="mt-2 font-medium">{item.title || item.tag_info || short(item.id, 10)}</h3>
            <p className="text-xs text-zinc-500">{item.view_count || 0} views · {item.verified_count || 0} verified</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => actions.copy(item.checkout_url)} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">Copy URL</button>
            <button onClick={() => actions.toggleCheckout(item)} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">{item.is_active ? 'Disable' : 'Enable'}</button>
            <button onClick={() => actions.deleteCheckout(item)} className="px-3 py-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs">Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function LegacyMailboxes({ items, actions }) {
  const [parser, setParser] = useState('chime');
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Select value={parser} onChange={setParser} options={[['chime', 'Chime'], ['cashapp', 'CashApp']]} />
        <button onClick={() => actions.connectMailbox(parser)} className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-500 text-sm">Connect Outlook</button>
      </div>
      {!items.length ? <Empty text="No mailboxes connected." /> : items.map((mailbox) => (
        <div key={mailbox.id} className="border border-zinc-800 rounded bg-zinc-950 p-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-medium">{mailbox.email_address}</h3>
            <p className="text-xs text-zinc-500">{mailbox.parser_type} · {mailbox.total_messages_processed || 0} processed · {mailbox.total_receipts_matched || 0} matched</p>
          </div>
          <button onClick={() => actions.syncMailbox(mailbox)} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">Sync</button>
        </div>
      ))}
    </div>
  );
}

function LegacyEvents({ items }) {
  if (!items.length) return <Empty text="No mail events." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs text-zinc-500"><tr><th className="text-left py-2">Subject</th><th className="text-left py-2">Amount</th><th className="text-left py-2">Status</th><th className="text-left py-2">Created</th></tr></thead>
        <tbody className="divide-y divide-zinc-800">
          {items.map((event) => (
            <tr key={event.id}>
              <td className="py-2">{event.subject || '-'}</td>
              <td className="py-2">{event.amount_parsed ? money(event.amount_parsed) : '-'}</td>
              <td className="py-2">{event.match_status || event.parse_status || '-'}</td>
              <td className="py-2 text-zinc-400">{dateTime(event.created_at || event.received_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LegacyClaims({ items, actions }) {
  if (!items.length) return <Empty text="No pending claims." />;
  return (
    <div className="grid gap-3">
      {items.map((claim) => (
        <div key={claim.id} className="border border-zinc-800 rounded bg-zinc-950 p-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-medium">{claim.first_name} {claim.last_name}</h3>
            <p className="text-xs text-zinc-500">{claim.result} · {dateTime(claim.created_at)}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => actions.approveClaim(claim)} className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-xs">Approve</button>
            <button onClick={() => actions.rejectClaim(claim)} className="px-3 py-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs">Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function TransactionDrawer({ state, loading, onClose, onCopy }) {
  const tx = state?.tx;
  const events = state?.events || [];
  const statusToneName = tx?.status === 'SUCCESS' ? 'emerald' : tx?.status === 'PENDING' ? 'amber' : 'rose';
  const styles = toneClasses[statusToneName] || toneClasses.rose;
  return (
    <div className="fixed inset-0 z-50 bg-black/75 md:flex md:justify-end" onClick={onClose}>
      <div className="h-full w-full overflow-y-auto border-l border-white/10 bg-slate-950 shadow-2xl md:max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className={`sticky top-0 z-10 border-b border-white/10 bg-gradient-to-br ${styles.panel} bg-slate-950 p-4 sm:p-5`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {tx?.status && <Badge className={statusTone(tx.status)}>{tx.status}</Badge>}
                {tx?.callbackState && <Badge className={callbackTone(tx.callbackState)}>{tx.callbackState}</Badge>}
              </div>
              <p className="text-xs text-slate-500">Transaction</p>
              <h2 className="break-all font-mono text-sm text-slate-100">{tx?.txId || 'Loading'}</h2>
              {tx && (
                <p className="mt-2 text-xl font-semibold text-slate-50">{money(tx.amount)} <span className="text-sm font-normal text-slate-400">{tx.method}</span></p>
              )}
            </div>
            <button onClick={onClose} className="rounded bg-white/10 p-2 text-slate-200 hover:bg-white/15" aria-label="Close transaction detail">
              <X size={18} />
            </button>
          </div>
          {tx && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              <button onClick={() => onCopy(tx.orderNo || tx.depositId)} className="rounded border border-white/10 bg-white/5 px-3 py-2 text-left text-xs text-slate-300">
                <span className="block text-slate-500">Order</span>
                <span className="block truncate font-mono text-slate-100">{tx.orderNo || tx.depositId || '-'}</span>
              </button>
              <button onClick={() => onCopy(tx.depositId)} className="rounded border border-white/10 bg-white/5 px-3 py-2 text-left text-xs text-slate-300">
                <span className="block text-slate-500">Deposit</span>
                <span className="block truncate font-mono text-slate-100">{tx.depositId || '-'}</span>
              </button>
              <button onClick={() => onCopy(tx.providerCode)} className="col-span-2 rounded border border-white/10 bg-white/5 px-3 py-2 text-left text-xs text-slate-300 sm:col-span-1">
                <span className="block text-slate-500">Provider</span>
                <span className="block truncate font-mono text-slate-100">{tx.providerCode || '-'}</span>
              </button>
            </div>
          )}
        </div>
        {loading || !tx ? <LoadingPanel /> : (
          <div className="space-y-5 p-3 sm:p-5">
            <section className="grid sm:grid-cols-3 gap-3">
              <Metric label="Status" value={tx.status} tone={statusToneName} icon={Gauge} />
              <Metric label="Amount" value={money(tx.amount)} tone="cyan" icon={WalletCards} />
              <Metric label="Paid in" value={secondsLabel(tx.timeToPaidSeconds)} tone="violet" icon={Activity} />
            </section>
            <Panel title="Identifiers">
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                {[
                  ['Deposit ID', tx.depositId],
                  ['Order ID', tx.orderNo],
                  ['Client', tx.clientName || tx.clientId],
                  ['Method', tx.method],
                  ['Provider', tx.providerCode],
                  ['Callback', tx.callbackState],
                ].map(([label, value]) => (
                  <button key={label} onClick={() => onCopy(value)} className="text-left border border-zinc-800 rounded p-3 bg-zinc-950 hover:bg-zinc-900">
                    <p className="text-xs text-zinc-500">{label}</p>
                    <p className="font-mono text-xs text-zinc-200 break-all mt-1">{value || '-'}</p>
                  </button>
                ))}
              </div>
            </Panel>
            {tx.card && (
              <Panel title="Card Comparison">
                <div className="grid md:grid-cols-3 gap-3 text-sm">
                  {Object.entries(tx.card).map(([key, value]) => (
                    <Stat key={key} label={key} value={value == null ? '-' : String(value)} />
                  ))}
                </div>
              </Panel>
            )}
            <Panel title="Timeline">
              {events.length === 0 ? <Empty text="No timeline events yet." /> : (
                <ol className="space-y-3">
                  {events.map((event) => (
                    <li key={event.eventId} className="border-l-2 border-zinc-700 pl-4 pb-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium">{event.message || event.eventType}</p>
                        <span className="text-xs text-zinc-500">{dateTime(event.createdAt)}</span>
                      </div>
                      <p className="font-mono text-xs text-zinc-500 mt-1">{event.eventType}</p>
                      {event.data && Object.keys(event.data).length > 0 && (
                        <pre className="mt-2 bg-zinc-900 rounded p-3 text-xs text-zinc-300 overflow-x-auto">{JSON.stringify(event.data, null, 2)}</pre>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}

function ClientEditor({ editing, onClose, onSave }) {
  const [name, setName] = useState(editing?.name || '');
  const [webhookUrl, setWebhookUrl] = useState(editing?.webhookUrl || '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onSave({ name: name.trim(), webhookUrl: webhookUrl.trim(), isActive }, editing);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">{editing ? 'Edit Client' : 'Add Client'}</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white">Close</button>
        </div>
        {error && <div className="border border-red-500/30 bg-red-500/10 text-red-300 rounded p-3 text-sm">{error}</div>}
        <label className="block">
          <span className="text-xs text-zinc-500">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 outline-none focus:ring-2 focus:ring-emerald-500" />
        </label>
        <label className="block">
          <span className="text-xs text-zinc-500">Webhook URL</span>
          <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} type="url" className="mt-1 w-full px-3 py-2 rounded bg-zinc-900 border border-zinc-700 outline-none focus:ring-2 focus:ring-emerald-500" />
        </label>
        {editing && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs">Cancel</button>
          <button disabled={saving} className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-sm font-medium">{saving ? 'Saving' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

function SnippetModal({ client, onClose, onCopy }) {
  const base = process.env.REACT_APP_BACKEND_URL || 'https://gatewayapi.1tappay.vip';
  const snippet = `curl -X POST "${base}/api/gateway/v1/create-link" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${client.apiKey || '<copy-api-key>'}" \\
  -d '{"depositId":"order_123","method":"card","amount":20}'`;
  return (
    <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Integration Snippet</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">Close</button>
        </div>
        <pre className="mt-4 bg-zinc-900 rounded p-4 text-xs text-zinc-200 overflow-x-auto whitespace-pre-wrap">{snippet}</pre>
        <div className="mt-4 flex justify-end">
          <button onClick={() => onCopy(snippet)} className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-sm">Copy</button>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, tone = 'cyan', icon: Icon = Activity }) {
  const normalized = tone === 'red' ? 'rose' : tone;
  const styles = toneClasses[normalized] || toneClasses.cyan;
  return (
    <div className={`min-w-40 rounded border border-white/10 bg-gradient-to-br ${styles.panel} p-3 shadow-lg shadow-black/20 sm:min-w-0`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-slate-500">{label}</p>
        <span className={`rounded p-1.5 ring-1 ${styles.soft}`}>
          <Icon size={15} />
        </span>
      </div>
      <p className={`mt-2 text-xl font-semibold ${styles.text}`}>{value}</p>
    </div>
  );
}

function Panel({ title, action, className = '', children }) {
  return (
    <section className={`rounded border border-white/10 bg-slate-950/70 p-4 shadow-xl shadow-black/20 ${className}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-semibold text-slate-100">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function MiniBars({ rows, valueKey }) {
  if (!rows?.length) return <Empty text="No data in this window." />;
  const max = Math.max(...rows.map((r) => Number(r[valueKey] || 0)), 1);
  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const value = Number(row[valueKey] || 0);
        return (
          <div key={row.key}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-zinc-300">{row.key}</span>
              <span className="text-zinc-500">{valueKey === 'volume' ? money(value) : value}</span>
            </div>
            <div className="h-2 overflow-hidden rounded bg-white/10">
              <div className="h-full rounded bg-gradient-to-r from-emerald-300 via-cyan-300 to-violet-300" style={{ width: `${Math.max(3, (value / max) * 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded border border-white/10 bg-slate-950 px-3 py-2 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-300/20">
      {options.map(([v, label]) => <option key={`${v}-${label}`} value={v}>{label}</option>)}
    </select>
  );
}

function Input({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded border border-white/10 bg-slate-950 py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-600 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-300/20" />
    </div>
  );
}

function Badge({ className, children }) {
  return <span className={`inline-flex px-2 py-0.5 rounded ring-1 text-[11px] font-medium ${className}`}>{children}</span>;
}

function Stat({ label, value }) {
  return (
    <div className="min-w-0 rounded border border-white/10 bg-white/5 p-2">
      <p className="text-[10px] uppercase text-slate-500">{label}</p>
      <p className="mt-1 truncate text-sm font-medium text-slate-200">{value}</p>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded border border-dashed border-white/10 bg-white/[0.02] p-6 text-center text-sm text-slate-500">{text}</div>;
}

function LoadingPanel() {
  return <div className="rounded border border-white/10 bg-slate-950/70 p-8 text-center text-sm text-slate-500">Loading...</div>;
}
